/** Laravel API base — used for login / register / account / catalog / cart */

import { getAuthHeaders, getCartToken, storeCartToken } from './cartToken';

const envApiUrl = process.env.REACT_APP_API_URL?.trim();

const PRODUCTION_API_URL = 'https://api.ammachekku.com/api';

function getFallbackApiUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // Same-origin /api during local CRA (see src/setupProxy.js) — avoids CORS
    if (host === 'localhost' || host === '127.0.0.1') {
      return '/api';
    }
  }

  return PRODUCTION_API_URL;
}

export const API_URL = (envApiUrl || getFallbackApiUrl()).replace(/\/+$/, '');

export { PRODUCTION_API_URL };

async function parseJsonResponse(response) {
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (data?.cart_token) {
    storeCartToken(data.cart_token);
  }

  if (!response.ok) {
    const message =
      data?.message ||
      (data?.errors && Object.values(data.errors).flat().join(' ')) ||
      `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

async function fetchJson(url, { headers, method = 'GET', body, timeoutMs = 12000 } = {}) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method,
      headers,
      body,
      signal: controller.signal,
    });
    return parseJsonResponse(response);
  } finally {
    window.clearTimeout(timer);
  }
}

export async function apiGet(path, { auth = false, timeoutMs = 12000, baseUrl } = {}) {
  const headers = auth
    ? getAuthHeaders()
    : { Accept: 'application/json' };

  const root = (baseUrl || API_URL).replace(/\/+$/, '');
  const url = `${root}${path.startsWith('/') ? path : `/${path}`}`;

  try {
    return await fetchJson(url, { headers, timeoutMs });
  } catch (error) {
    // Local Laravel (:8000) often isn't running — retry via CRA proxy or live API
    const isDirectLocal = /localhost:8000|127\.0\.0\.1:8000/i.test(root);
    if (isDirectLocal) {
      try {
        return await fetchJson(
          `/api${path.startsWith('/') ? path : `/${path}`}`,
          { headers, timeoutMs }
        );
      } catch {
        return fetchJson(
          `${PRODUCTION_API_URL}${path.startsWith('/') ? path : `/${path}`}`,
          { headers, timeoutMs }
        );
      }
    }
    throw error;
  }
}

export async function apiPost(path, body, { auth = false } = {}) {
  const headers = auth
    ? { ...getAuthHeaders(), 'Content-Type': 'application/json' }
    : {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

  const payload =
    auth && body && typeof body === 'object'
      ? { ...body, cart_token: body.cart_token || getCartToken() }
      : body;

  const response = await fetch(`${API_URL}${path.startsWith('/') ? path : `/${path}`}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  return parseJsonResponse(response);
}

export default API_URL;
